import type { MethodSort, PaymentsCard, User } from '@library/types/payments';
import {
  useDeleteCardForUserMutation,
  useGetCardsForUserQuery
} from '@bo-services/paymentsApi';
import { useMemo, useState } from 'react';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { Loading } from '@library/components/Loading';
import { MethodsHeader } from '@library/components/table/payments/methods/MethodsHeader';
import { MethodsRow } from '@library/components/table/payments/methods/MethodsRow';
import { PaymentMethodDetailModal } from '@library/components/modals/PaymentMethodDetailModal/PaymentMethodDetailModal';

export interface PaymentMethodsTableProps {
  user: User;
  userQuery?: string;
}

export const PaymentMethodsTable = ({
  user,
  userQuery = ''
}: PaymentMethodsTableProps) => {
  const [sort, setSort] = useState<MethodSort>({
    sortBy: '',
    sortDirection: 'asc'
  });

  const queryObj = useMemo(() => {
    return {
      user_uuid: user.uuid,
      email: userQuery || undefined
    };
  }, [user, userQuery]);

  const { data, isLoading } = useGetCardsForUserQuery(queryObj);
  const [deleteCard] = useDeleteCardForUserMutation();

  const cards = data?.cards || [];

  const [cardToDelete, setCardToDelete] = useState<PaymentsCard | undefined>();
  const [modalCard, setModalCard] = useState<PaymentsCard | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  const clearCardToDelete = () => setCardToDelete(undefined);

  const submitCardForDeletion = () => {
    if (!cardToDelete) {
      return;
    }

    setIsDeleting(true);
    deleteCard({
      ...cardToDelete,
      userUuid: user.uuid
    })
      // we rely on the cache-break of card fetch to update the UI after deletion
      .then()
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert('a failure has occurred while trying to delete the card');
      })
      .finally(() => {
        setIsDeleting(false);
      });

    clearCardToDelete();
  };

  if (isLoading) {
    return <Loading loadingMessage="Loading payment methods" />;
  }

  return (
    <>
      {modalCard && (
        <PaymentMethodDetailModal
          card={modalCard}
          onCancelModal={() => setModalCard(undefined)}
        />
      )}

      <ConfirmationModal
        isOpen={!!cardToDelete}
        label="Are you sure?"
        confirmationCopy="Remove"
        onCancelModal={clearCardToDelete}
        handleConfirm={submitCardForDeletion}
      >
        Are you sure you want to delete this payment method?
      </ConfirmationModal>

      <div className="text-white">Methods</div>
      <table className="w-full">
        <MethodsHeader handleSort={setSort} sort={sort} />

        <tbody className="text-2xs xl:text-xs">
          {cards?.map((card) => (
            <MethodsRow
              method={card}
              key={card.id}
              onDestroy={() => setCardToDelete(card)}
              onRowClick={() => setModalCard(card)}
              isDeleting={isDeleting}
            />
          ))}
        </tbody>
      </table>
    </>
  );
};
